import { useEffect } from 'react';
import { Navigate, useNavigate, useOutletContext } from 'react-router-dom';
import { Button, Pill, Spinner } from '../components/primitives';
import type { SignedInShellOutletContext } from '../components/shell/SignedInShell';
import { useShellSession } from '../components/shell/useShellSession';
import { useOwnerContactVM } from '../lib/vm/useOwnerContactVM';
import './Paywall.css';

/**
 * `/paywall` — the surface a suspended user lands on after switching INTO a
 * suspended org (via `IdentityFlyout`) or while their active org's membership
 * has `suspended_at != null`. Renders inside `SignedInShell` so the org
 * switcher + sign-out remain reachable from the header chrome.
 *
 * Spec source: `/Users/lane/Documents/Penrose/ide-mock/features/billing-and-
 * access-gating/prd.md` + `ux.md` ("Surface 2 — Paywall"). The web surface is
 * spec-identical to the IDE surface except for the surrounding chrome.
 *
 * Content shown (per OQ-2 resolution — reason-agnostic copy):
 *   - Org name (large) + `Suspended` pill.
 *   - Single sentence: `Your access to <Org> is paused.`
 *   - Owner contact block (display name + email) via
 *     `useOwnerContactVM(orgId)`. On RPC failure renders the generic copy
 *     `Contact the org owner to restore access.`
 *   - `Open org switcher` button (primary).
 *   - `Sign out` button (secondary).
 *
 * No org data is fetched. If the viewer's active org is NOT suspended, the
 * page redirects to `/dashboard` (defensive — direct nav to `/paywall` when
 * the user has access).
 */
export function Paywall() {
  const navigate = useNavigate();
  const session = useShellSession();
  const shell = useOutletContext<SignedInShellOutletContext | undefined>();

  const activeOrgId = shell?.activeOrgId ?? null;
  const activeOrgName = shell?.activeOrgName ?? null;

  const activeMembership = session.memberships.find(
    (m) => m.org_id === activeOrgId
  );
  const isSuspended = !!activeMembership?.suspended_at;
  const sessionLoading = session.status === 'loading';

  const owner = useOwnerContactVM(isSuspended ? activeOrgId : null);

  // If the active org isn't suspended (e.g. the user navigated directly to
  // /paywall while they have access), bounce back to /dashboard. Wait for the
  // session to resolve first so we don't redirect on the loading flash.
  useEffect(() => {
    if (sessionLoading) return;
    if (!activeOrgId) return;
    if (!isSuspended) {
      navigate('/dashboard', { replace: true });
    }
  }, [sessionLoading, activeOrgId, isSuspended, navigate]);

  // Defensive — if the session resolved with no active org at all, send the
  // user back to the dashboard so the shell's no-org chrome handles it.
  if (!sessionLoading && !activeOrgId) {
    return <Navigate to="/dashboard" replace />;
  }

  const orgName = activeOrgName ?? '…';

  function handleOpenSwitcher() {
    shell?.openIdentityFlyout();
  }

  async function handleSignOut() {
    if (!shell) return;
    await shell.signOut();
  }

  return (
    <section className="paywall" data-testid="paywall-page">
      <header className="paywall-header">
        <h1 className="paywall-org" data-testid="paywall-org-name">
          {orgName}
        </h1>
        <Pill status="suspended" data-testid="paywall-status-pill">
          Suspended
        </Pill>
      </header>

      <p className="paywall-reason" data-testid="paywall-reason">
        Your access to {orgName} is paused.
      </p>

      <div className="paywall-owner" data-testid="paywall-owner-block">
        {owner.loading ? (
          <Spinner size="sm" label="Loading owner contact" />
        ) : owner.error || (!owner.contact.display_name && !owner.contact.email) ? (
          <p
            className="paywall-owner-fallback"
            data-testid="paywall-owner-fallback"
          >
            Contact the org owner to restore access.
          </p>
        ) : (
          <>
            <p className="paywall-owner-lead">
              Contact your org owner to restore access:
            </p>
            {owner.contact.display_name ? (
              <p
                className="paywall-owner-name"
                data-testid="paywall-owner-name"
              >
                {owner.contact.display_name}
              </p>
            ) : null}
            {owner.contact.email ? (
              <p className="paywall-owner-email">
                <a
                  href={`mailto:${owner.contact.email}`}
                  data-testid="paywall-owner-email"
                >
                  {owner.contact.email}
                </a>
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="paywall-actions">
        <Button
          variant="primary"
          onClick={handleOpenSwitcher}
          data-testid="paywall-open-switcher"
        >
          Open org switcher
        </Button>
        <Button
          variant="default"
          onClick={() => void handleSignOut()}
          data-testid="paywall-sign-out"
        >
          Sign out
        </Button>
      </div>
    </section>
  );
}
